/**
 * Company Upload File Mock Data Panel
 *
 * 开发环境下的快捷上传工具
 * 提供可视化的面板用于快速上传企业证明文件
 *
 * @module mockDataGenerator.demo
 */

import React from "react";
import { triggerFileUpload } from "../../utils/mockFileUploadTrigger";
import { Button, InputNumber, message } from "antd";
import { useEffect, useRef, useState } from "react";
import { delay } from "@/utils";

// 定义上传状态
export enum UploadStatus {
  PENDING = "pending",
  UPLOADING = "uploading",
  SUCCESS = "success",
  ERROR = "error",
}

// 定义文件项接口
export type FileItem = {
  id: string;
  name: string;
  fileName?: string;
  status: UploadStatus;
  progress?: number;
  error?: string;
  url?: string;
};

export enum FileUploadType {
  COMPANY = "companyDocuments",
  CORPORATE = "corporateShareholderDocs",
  INDIVIDUAL = "individualShareholderDocs",
  SIGNATURE = "signedDocuments",
  OTHER = "otherDocuments",
}

interface CompanyUploadFileMockDataPanelProps {
  companyFiles: FileItem[];
  setCompanyFiles: React.Dispatch<React.SetStateAction<FileItem[]>>;
  corporateFiles: FileItem[];
  setCorporateFiles: React.Dispatch<React.SetStateAction<FileItem[]>>;
  individualFiles: FileItem[];
  setIndividualFiles: React.Dispatch<React.SetStateAction<FileItem[]>>;
  signatureFiles: FileItem[];
  setSignatureFiles: React.Dispatch<React.SetStateAction<FileItem[]>>;
  otherFiles: FileItem[];
  setOtherFiles: React.Dispatch<React.SetStateAction<FileItem[]>>;
  handleAddFile: (sectionType: FileUploadType) => void;
}

/**
 * 企业上传文件 Mock 数据生成工具面板
 */
export function CompanyUploadFileMockDataPanel({
  companyFiles,
  setCompanyFiles,
  corporateFiles,
  setCorporateFiles,
  individualFiles,
  setIndividualFiles,
  signatureFiles,
  setSignatureFiles,
  otherFiles,
  setOtherFiles,
  handleAddFile,
}: CompanyUploadFileMockDataPanelProps) {
  const [visible, setVisible] = useState(false);
  const [uploading, setUploading] = useState(false);

  // 文件分类配置
  const [companyFileCount, setCompanyFileCount] = useState(3);
  const [corporateFileCount, setCorporateFileCount] = useState(2);
  const [individualFileCount, setIndividualFileCount] = useState(2);
  const [signatureFileCount, setSignatureFileCount] = useState(3);
  const [otherFileCount, setOtherFileCount] = useState(0);

  // 使用ref存储最新的files（避免闭包问题）
  const companyFilesRef = useRef(companyFiles);
  useEffect(() => {
    companyFilesRef.current = companyFiles;
  }, [companyFiles]);

  /* ============================================================
   * 文件上传操作
   * ========================================================== */

  /**
   * 按分类设置文件（触发真实上传，获取真实fileId）
   */
  const handleUploadByCategory = async () => {
    setUploading(true);
    message.loading({
      content: "正在触发上传...",
      key: "upload-category",
      duration: 0,
    });

    try {
      // 步骤1：触发第一个文件真实上传
      console.log("🔄 触发第一个文件上传...");
      await triggerFileUpload({
        fileUrl: "/icon.png",
        fileName: "company-document.png",
        selector: 'input[type="file"]',
      });

      // 步骤2：等待上传完成，获取真实fileId
      message.loading({
        content: "等待上传完成...",
        key: "upload-category",
        duration: 0,
      });

      let fileId: string | undefined;
      let fileName = "company-document.png";
      const maxAttempts = 20;

      for (let i = 0; i < maxAttempts; i++) {
        await delay(500);

        // 使用ref获取最新的files（避免闭包）
        const latestFiles = companyFilesRef.current;
        const uploadedFile = latestFiles[0];

        if (uploadedFile?.url && uploadedFile.status === UploadStatus.SUCCESS) {
          fileId = uploadedFile.url;
          fileName = uploadedFile.fileName || fileName;
          console.log("✅ 上传成功，fileId:", fileId);
          break;
        }

        console.log(`⏳ 等待上传完成... (${i + 1}/${maxAttempts})`);
      }

      if (!fileId) {
        throw new Error("上传超时，未获取到fileId");
      }

      message.loading({
        content: "正在设置文件...",
        key: "upload-category",
        duration: 0,
      });

      // 步骤3：先为"其他文件"添加占位符
      for (let i = 0; i < otherFileCount; i++) {
        handleAddFile(FileUploadType.OTHER);
        await delay(100);
      }

      // 步骤4：用真实fileId设置所有文件
      const updateFiles = (
        _files: FileItem[],
        setter: React.Dispatch<React.SetStateAction<FileItem[]>>,
        count: number
      ) => {
        setter((prev) =>
          prev.map((item, index) => {
            if (index < count) {
              return {
                ...item,
                status: UploadStatus.SUCCESS,
                progress: 100,
                url: fileId, // 使用真实上传获取的fileId
                fileName: fileName,
              };
            }
            return item;
          })
        );
      };

      let totalCount = 0;

      // 公司文件（第一个已经上传了，从第二个开始设置）
      if (companyFileCount > 1) {
        setCompanyFiles((prev) =>
          prev.map((item, index) => {
            if (index > 0 && index < companyFileCount) {
              return {
                ...item,
                status: UploadStatus.SUCCESS,
                progress: 100,
                url: fileId,
                fileName: fileName,
              };
            }
            return item;
          })
        );
      }
      totalCount += companyFileCount;
      console.log(`✅ 已设置 ${companyFileCount} 个公司文件`);

      // 法人股东文件
      if (corporateFileCount > 0) {
        updateFiles(corporateFiles, setCorporateFiles, corporateFileCount);
        totalCount += corporateFileCount;
        console.log(`✅ 已设置 ${corporateFileCount} 个法人股东文件`);
      }

      // 个人股东文件
      if (individualFileCount > 0) {
        updateFiles(individualFiles, setIndividualFiles, individualFileCount);
        totalCount += individualFileCount;
        console.log(`✅ 已设置 ${individualFileCount} 个个人股东文件`);
      }

      // 签署文件
      if (signatureFileCount > 0) {
        updateFiles(signatureFiles, setSignatureFiles, signatureFileCount);
        totalCount += signatureFileCount;
        console.log(`✅ 已设置 ${signatureFileCount} 个签署文件`);
      }

      // 其他文件（需要等待添加完成后再设置）
      if (otherFileCount > 0) {
        await delay(200);
        updateFiles(otherFiles, setOtherFiles, otherFileCount);
        totalCount += otherFileCount;
        console.log(`✅ 已设置 ${otherFileCount} 个其他文件`);
      }

      message.success({
        content: `✅ 已设置 ${totalCount} 个文件`,
        key: "upload-category",
        duration: 3,
      });
      console.log(`✅ 总共设置 ${totalCount} 个文件`);
    } catch (error) {
      message.error({
        content: "❌ 设置文件失败",
        key: "upload-category",
        duration: 3,
      });
      console.error("❌ 设置文件失败:", error);
    } finally {
      setUploading(false);
    }
  };

  /**
   * 上传所有文件（触发真实上传，获取真实fileId）
   */
  const handleUploadAll = async () => {
    setUploading(true);
    message.loading({
      content: "正在触发上传...",
      key: "upload-all",
      duration: 0,
    });

    try {
      // 步骤1：触发第一个文件真实上传
      console.log("🔄 触发第一个文件上传...");
      await triggerFileUpload({
        fileUrl: "/icon.png",
        fileName: "company-document.png",
        selector: 'input[type="file"]',
      });

      // 步骤2：等待上传完成，获取真实fileId
      message.loading({
        content: "等待上传完成...",
        key: "upload-all",
        duration: 0,
      });

      let fileId: string | undefined;
      let fileName = "company-document.png";
      const maxAttempts = 20;

      for (let i = 0; i < maxAttempts; i++) {
        await delay(500);

        // 使用ref获取最新的files（避免闭包）
        const latestFiles = companyFilesRef.current;
        const uploadedFile = latestFiles[0];

        if (uploadedFile?.url && uploadedFile.status === UploadStatus.SUCCESS) {
          fileId = uploadedFile.url;
          fileName = uploadedFile.fileName || fileName;
          console.log("✅ 上传成功，fileId:", fileId);
          break;
        }

        console.log(`⏳ 等待上传完成... (${i + 1}/${maxAttempts})`);
      }

      if (!fileId) {
        throw new Error("上传超时，未获取到fileId");
      }

      // 步骤3：设置所有文件
      message.loading({
        content: "正在设置所有文件...",
        key: "upload-all",
        duration: 0,
      });

      // 用真实fileId设置所有文件
      const setAll = (
        setter: React.Dispatch<React.SetStateAction<FileItem[]>>
      ) => {
        setter((prev) =>
          prev.map((item) => ({
            ...item,
            status: UploadStatus.SUCCESS,
            progress: 100,
            url: fileId, // 使用真实上传获取的fileId
            fileName: fileName,
          }))
        );
      };

      setAll(setCompanyFiles);
      setAll(setCorporateFiles);
      setAll(setIndividualFiles);
      setAll(setSignatureFiles);
      setAll(setOtherFiles);

      const totalCount =
        companyFiles.length +
        corporateFiles.length +
        individualFiles.length +
        signatureFiles.length +
        otherFiles.length;

      message.success({
        content: `✅ 已设置 ${totalCount} 个文件`,
        key: "upload-all",
        duration: 3,
      });
      console.log(`✅ 已设置 ${totalCount} 个文件`);
    } catch (error) {
      message.error({
        content: "❌ 设置文件失败",
        key: "upload-all",
        duration: 3,
      });
      console.error("❌ 设置文件失败:", error);
    } finally {
      setUploading(false);
    }
  };

  /**
   * 清空所有文件
   */
  const handleClearAll = () => {
    // 刷新页面来清空所有文件
    window.location.reload();
  };

  const handleToggle = () => {
    setVisible(!visible);
  };

  return (
    <>
      {/* 浮动按钮 */}
      {!visible && (
        <div
          className="fixed bottom-8 right-8 z-[9999] group cursor-pointer"
          onClick={handleToggle}
          style={{
            width: "64px",
            height: "64px",
          }}
        >
          <div
            className="absolute inset-0 rounded-full transition-all duration-300 group-hover:scale-110"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              boxShadow:
                "0 10px 25px -5px rgba(102, 126, 234, 0.5), 0 8px 10px -6px rgba(102, 126, 234, 0.3)",
            }}
          />
          <div
            className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{
              background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              boxShadow: "0 15px 35px -5px rgba(118, 75, 162, 0.6)",
            }}
          />
          <div className="relative w-full h-full flex items-center justify-center text-3xl transition-transform duration-300 group-hover:scale-110 group-active:scale-95">
            📎
          </div>
          <div
            className="absolute inset-0 rounded-full animate-ping opacity-20"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              animationDuration: "3s",
            }}
          />
        </div>
      )}

      {/* 主面板 */}
      {visible && (
        <div
          className="fixed bottom-8 right-8 z-[9999] bg-white rounded-lg shadow-2xl"
          style={{
            width: "320px",
            maxHeight: "600px",
            display: "flex",
            flexDirection: "column",
            border: "1px solid #d9d9d9",
          }}
        >
          {/* 标题栏 */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{
              borderBottom: "1px solid #f0f0f0",
              backgroundColor: "#fafafa",
              borderRadius: "8px 8px 0 0",
            }}
          >
            <span className="font-semibold text-base">
              📎 证明文件 Mock工具
            </span>
            <Button size="small" onClick={handleToggle}>
              ✕
            </Button>
          </div>

          {/* 内容区域 */}
          <div
            className="overflow-y-auto p-4 space-y-4"
            style={{ maxHeight: "500px" }}
          >
            {/* 文件数量配置 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                🔢 文件数量配置
              </div>

              {/* 公司文件 */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">公司文件</span>
                <InputNumber
                  size="small"
                  min={0}
                  max={12}
                  value={companyFileCount}
                  onChange={(val) => setCompanyFileCount(val || 0)}
                  style={{ width: "70px" }}
                />
              </div>

              {/* 法人股东文件 */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">法人股东文件</span>
                <InputNumber
                  size="small"
                  min={0}
                  max={5}
                  value={corporateFileCount}
                  onChange={(val) => setCorporateFileCount(val || 0)}
                  style={{ width: "70px" }}
                />
              </div>

              {/* 个人股东文件 */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">个人股东文件</span>
                <InputNumber
                  size="small"
                  min={0}
                  max={2}
                  value={individualFileCount}
                  onChange={(val) => setIndividualFileCount(val || 0)}
                  style={{ width: "70px" }}
                />
              </div>

              {/* 签署文件 */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">签署文件</span>
                <InputNumber
                  size="small"
                  min={0}
                  max={8}
                  value={signatureFileCount}
                  onChange={(val) => setSignatureFileCount(val || 0)}
                  style={{ width: "70px" }}
                />
              </div>

              {/* 其他文件 */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">其他文件</span>
                <InputNumber
                  size="small"
                  min={0}
                  max={10}
                  value={otherFileCount}
                  onChange={(val) => setOtherFileCount(val || 0)}
                  style={{ width: "70px" }}
                />
              </div>
            </div>

            {/* 分割线 */}
            <div className="border-t border-gray-200" />

            {/* 文件上传操作 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                📤 文件上传
              </div>
              <Button
                size="small"
                block
                type="primary"
                onClick={handleUploadByCategory}
                loading={uploading}
              >
                按配置上传
              </Button>
              <Button
                size="small"
                block
                onClick={handleUploadAll}
                loading={uploading}
              >
                上传所有文件
              </Button>
            </div>

            {/* 分割线 */}
            <div className="border-t border-gray-200" />

            {/* 说明 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                ℹ️ 使用说明
              </div>
              <div className="text-xs text-gray-600 space-y-1">
                <div>• 在上方设置每个分类需要上传几个文件</div>
                <div>• 点击"按配置上传"按你的配置上传</div>
                <div>• 点击"上传所有文件"上传全部27个</div>
                <div>• 每个文件都会上传同一张图片（/icon.png）</div>
              </div>
            </div>
          </div>

          {/* 清空按钮 */}
          <div className="px-4 pb-4 pt-2 border-t">
            <Button danger size="small" block onClick={handleClearAll}>
              清空所有文件（刷新页面）
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default CompanyUploadFileMockDataPanel;
